/**
 * ===========================================================================
 *  AI BLACKBOARD — the per-entity working memory of every non-player unit
 * ===========================================================================
 *
 * Structure-of-arrays, indexed by entity id, allocated once at boot and never
 * again. Nothing in `src/ai/**` may allocate inside a fixed step; if you need
 * a temporary, put it at module scope.
 *
 * A slot is bound to an entity by its `uid`. `Entities` recycles ids, so every
 * read must go through `ensure(e, id)` first — that is what wipes a dead man's
 * memory before the next recruit inherits his slot.
 *
 * `uid` is only the recycled-slot/ABA guard. Personality (speed, aggression,
 * courage, orbit direction, body clock phase) is derived from the entity's
 * battle-local `spawnKey`, not from identity. Same-seed retries therefore keep
 * the authored cast while every newly spawned entity still receives a fresh
 * global UID.
 */

export const ACT = {
  IDLE: 0,        // unaware — holds a stance, fidgets, watches
  FORM: 1,        // walking to its formation slot
  MARCH: 2,       // squad is advancing; hold the slot and move with it
  APPROACH: 3,    // aware, running at the target
  RING: 4,        // in the ring: orbiting at 2–4 m, waiting for a token
  PRESS: 5,       // brief threatening step in, then back out (no damage)
  WINDUP: 6,      // token held, blade going back
  SWING: 7,       // active frames
  RECOVER: 8,     // committed recovery, cannot be cancelled
  BLOCK: 9,       // guard up
  TAUNT: 10,      // shouting at the player
  FLINCH: 11,     // hit reaction
  LAUNCHED: 12,   // airborne after a launcher
  DOWN: 13,       // on the ground
  GETUP: 14,
  FLEE: 15,       // morale broken
  DEAD: 16,
  DUEL: 17,       // officer.js owns this unit's sub-state
};
export const ACT_NAME = ['idle', 'form', 'march', 'approach', 'ring', 'press', 'windup', 'swing',
  'recover', 'block', 'taunt', 'flinch', 'launched', 'down', 'getup', 'flee', 'dead', 'duel'];

/** True while the unit is committed and decide() must not interrupt it. */
export const LOCKED = new Uint8Array(18);
for (const a of [ACT.WINDUP, ACT.SWING, ACT.RECOVER, ACT.FLINCH, ACT.LAUNCHED, ACT.DOWN, ACT.GETUP, ACT.DEAD]) LOCKED[a] = 1;

export const ROLE = { GRUNT: 0, CAPTAIN: 1, OFFICER: 2, ARCHER: 3 };

/** Squad orders. */
export const ORDERS = { HOLD: 0, ADVANCE: 1, ESCORT: 2, ENGAGE: 3, ROUT: 4 };
export const FORM = { LINE: 0, COLUMN: 1, WEDGE: 2, BLOB: 3 };

export function hash32(n) {
  n = (n | 0) ^ 0x9e3779b9;
  n = Math.imul(n ^ (n >>> 16), 0x21f0aaad);
  n = Math.imul(n ^ (n >>> 15), 0x735a2d97);
  return (n ^ (n >>> 15)) >>> 0;
}
/** Deterministic 0..1 from a battle-local key + a salt. Never touches RNG. */
export function frac01(n, salt = 0) { return (hash32(n + Math.imul(salt + 1, 0x85ebca6b)) >>> 8) / 16777216; }

const CAP = 2600;

export class AIStore {
  constructor(cap = CAP) {
    const f32 = () => new Float32Array(cap);
    const i32 = () => new Int32Array(cap);
    const i16 = () => new Int16Array(cap);
    const i8 = () => new Int8Array(cap);
    const u8 = () => new Uint8Array(cap);
    this.cap = cap;
    this.uid = i32();
    this.spawnKey = new Uint32Array(cap);
    this.act = i8(); this.prevAct = i8(); this.actT = f32(); this.dur = f32();
    this.think = f32(); this.tgt = i32(); this.tgtUid = i32();
    this.ringA = f32(); this.ringR = f32(); this.ringDir = i8(); this.orbit = f32();
    this.sect = i8(); this.rank = u8();      // claimed ring sector + inner/outer rank
    this.cool = f32(); this.tok = u8(); this.tokT = f32();
    this.role = i8(); this.squad = i16(); this.slot = i16();
    this.spdMul = f32(); this.agg = f32(); this.crg = f32(); this.phase = f32();
    this.alert = u8(); this.homeX = f32(); this.homeZ = f32();
    this.dvx = f32(); this.dvz = f32();      // desired velocity, written by behaviours
    this.yawT = f32();                       // desired facing
    this.clip = i8();                        // which attack clip this swing uses
    this.flags = i32();                      // AI-private bits (see AIF)
    /** token bookkeeping, indexed by TARGET id */
    this.tokN = i32(); this.ringN = i32(); this.grantT = f32();
    /**
     * Ring SLOT bookkeeping, indexed by (TARGET id << 5) + sector.
     * Low 16 entries are the inner rank, high 16 the outer one. Rebuilt from
     * the claims every census, exactly like `tokN`, so a slot can never leak
     * when its holder is killed, knocked down or despawned.
     *
     * This is what turns "a wave that arrives from one side stays on that
     * side" into a ring: a man cannot stand where a man already stands, so a
     * crowd arriving from the east walks around to the empty west.
     */
    this.ringSlot = new Uint8Array(cap << 5);
  }

  /** Wipe the slot and roll a fresh personality for a newly-seen entity. */
  bind(e, id, role = ROLE.GRUNT) {
    const key = e.spawnKey[id];
    this.uid[id] = e.uid[id];
    this.spawnKey[id] = key;
    this.act[id] = ACT.IDLE; this.prevAct[id] = ACT.IDLE; this.actT[id] = 0; this.dur[id] = 0;
    this.tgt[id] = -1; this.tgtUid[id] = 0;
    this.sect[id] = -1; this.rank[id] = 0;
    this.tok[id] = 0; this.tokT[id] = 0; this.alert[id] = 0; this.flags[id] = 0;
    this.role[id] = role;
    this.squad[id] = -1; this.slot[id] = 0;
    this.homeX[id] = e.x[id]; this.homeZ[id] = e.z[id];
    this.dvx[id] = 0; this.dvz[id] = 0; this.yawT[id] = e.yaw[id];
    // ---- personality -------------------------------------------------
    // 0.86..1.14 on foot speed is what stops a charge arriving as a wall.
    this.spdMul[id] = 0.86 + frac01(key, 1) * 0.30;
    this.agg[id] = frac01(key, 2);                       // how eager to swing
    this.crg[id] = 0.30 + frac01(key, 3) * 0.62;         // morale floor
    this.phase[id] = frac01(key, 4) * 6.2832;            // body-clock offset
    this.ringDir[id] = frac01(key, 5) < 0.5 ? -1 : 1;
    // Inner-rank standing radius. It is deliberately capped BELOW the commit
    // range (R_ATTACK + 0.85 = 3.20 m in grunt.js): the old 2.15-3.50 spread,
    // plus +1.25 m of "crowded" padding, put most of a big ring permanently
    // outside its own attack test, so the mob got LESS dangerous the bigger it
    // grew. Depth now comes from the OUTER RANK, not from pushing everyone out.
    this.ringR[id] = 2.30 + frac01(key, 6) * 0.72;      // 2.30-3.02
    this.orbit[id] = 0.10 + frac01(key, 7) * 0.26;
    this.think[id] = 0.05 + frac01(key, 8) * 0.30;       // stagger first thought
    this.cool[id] = 0.4 + frac01(key, 9) * 1.4;
    this.clip[id] = hash32(key) & 3;
    return id;
  }

  ensure(e, id) { if (this.uid[id] !== e.uid[id]) this.bind(e, id); }
}

/** How many angular slots a ring is divided into (both ranks). */
export const SECTORS = 16;
export const SECT_A = 6.283185307179586 / SECTORS;
/** Bearing (target -> man) of the centre of sector `s`. */
export function sectAngle(s) { return s * SECT_A - Math.PI + SECT_A * 0.5; }
/** Sector index holding bearing `a` (radians, any range). */
export function sectOf(a) {
  let s = ((a + Math.PI) / SECT_A) | 0;
  s %= SECTORS; if (s < 0) s += SECTORS;
  return s;
}

/** AI-private flags (NOT the entity FLAG bitmask). */
export const AIF = {
  SHIELD: 1 << 0,       // carries a shield: blocks more often
  VETERAN: 1 << 1,      // slower to break, hits harder
  BANNER: 1 << 2,       // squad standard bearer: never leads a charge
  ENGAGED: 1 << 3,      // has been in melee at least once
  ROUTED: 1 << 4,       // morale has already broken once
};
